/*
<purpose>
  Polls getTicket while the open detail is in a transient/generating status,
  updating the detail pane the moment reanalysis or processing completes.
  Stops (and never starts) when the ticket is in a stable status — this is the
  edit-clobber guard: reps can only edit a suggestion_ready ticket, and we never
  re-fetch while they are editing.
</purpose>
<dependencies>smarttix.getTicket (services/api.ts); POLLING_INTERVAL_MS (constants.ts)</dependencies>
<constraints>
  - Poll only while status ∈ TRANSIENT_STATUSES; no-op for all stable statuses.
  - setSelectedTicket and uuid are read via refs so the interval closure is always
    current without re-creating the interval on each render (mirrors FR-T2 list poll).
  - Interval cleared on unmount AND when selectedTicket changes (selection change or
    status resolves) — no stale-closure leaks.
  - Edit-clobber guarantee: suggestion_ready (and all post-ready statuses) are NOT
    in TRANSIENT_STATUSES, so a rep editing a ready suggestion is never interrupted
    by an overwriting re-fetch.
</constraints>
*/
import { useEffect, useRef } from 'react';
import { TicketDetail } from '../types';
import { smarttix } from '../services/api';
import { POLLING_INTERVAL_MS } from '../constants';

export const TRANSIENT_STATUSES = new Set([
  'reanalyzing',
  'categorizing',
  'categorized',
  'generating_suggestion',
]);

export function useTicketDetailRefresh(
  selectedTicket: TicketDetail | null,
  setSelectedTicket: (t: TicketDetail) => void,
): void {
  // Latest-value refs so the interval closure is always current without recreating.
  const setterRef = useRef(setSelectedTicket);
  setterRef.current = setSelectedTicket;

  const ticketRef = useRef(selectedTicket);
  ticketRef.current = selectedTicket;

  useEffect(() => {
    const ticket = selectedTicket;
    if (!ticket || !TRANSIENT_STATUSES.has(ticket.status)) return;

    const id = setInterval(async () => {
      const current = ticketRef.current;
      if (!current) return;
      try {
        const fresh = await smarttix.getTicket(current.uuid);
        if (fresh) setterRef.current(fresh);
      } catch {
        // Keep existing detail on transient network error; interval retries next tick.
      }
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(id);
  // Re-runs when selectedTicket identity changes (uuid swap or status transition).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket]);
}
